/* global google */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

const MapContainer = ({
  showDeleteButton,
  onDeleteShape,
  onPolygonChange,
  shapesDeleted,
}) => {
  const center = { lat: 11.1271, lng: 78.6569 };

  const [isDrawing, setIsDrawing] = useState(false);

  const mapRef = useRef(null);

  // All completed polygons
  const polygonRefs = useRef([]);

  // Polygon currently being drawn
  const currentPolygonRef = useRef(null);

  // Google Maps event listeners
  const listenersRef = useRef([]);

  const map = useMap();
  const places = useMapsLibrary("places");

  // ----------------------------------------------------
  // Polygon options
  // ----------------------------------------------------

  const polygonOptions = {
    fillOpacity: 0.3,
    fillColor: "#ff0000",
    strokeColor: "#ff0000",
    strokeWeight: 2,
    draggable: true,
    editable: true,
  };

  // ----------------------------------------------------
  // Get polygon coordinates
  // ----------------------------------------------------

  const getPolygonCoordinates = useCallback((polygon) => {
    if (!polygon) return [];

    return polygon
      .getPath()
      .getArray()
      .map((latLng) => ({
        lat: latLng.lat(),
        lng: latLng.lng(),
      }));
  }, []);

  // ----------------------------------------------------
  // Send polygon coordinates to parent
  // ----------------------------------------------------

  const notifyPolygonChange = useCallback(() => {
    const allCoordinates = polygonRefs.current.flatMap((polygon) =>
      getPolygonCoordinates(polygon)
    );

    onPolygonChange(allCoordinates);
  }, [getPolygonCoordinates, onPolygonChange]);

  // ----------------------------------------------------
  // Add listeners to polygon
  // ----------------------------------------------------

  const addPolygonListeners = useCallback(
    (polygon) => {
      if (!polygon) return;

      const path = polygon.getPath();

      const updateCoordinates = () => {
        notifyPolygonChange();
      };

      const setAtListener = path.addListener(
        "set_at",
        updateCoordinates
      );

      const insertAtListener = path.addListener(
        "insert_at",
        updateCoordinates
      );

      const removeAtListener = path.addListener(
        "remove_at",
        updateCoordinates
      );

      const dragEndListener = polygon.addListener(
        "dragend",
        updateCoordinates
      );

      listenersRef.current.push(
        setAtListener,
        insertAtListener,
        removeAtListener,
        dragEndListener
      );
    },
    [notifyPolygonChange]
  );

  // ----------------------------------------------------
  // Start drawing
  // ----------------------------------------------------

  const startDrawing = useCallback(() => {
    // Remove temporary polygon if exists
    if (currentPolygonRef.current) {
      currentPolygonRef.current.setMap(null);
      currentPolygonRef.current = null;
    }

    setIsDrawing(true);
  }, []);

  // ----------------------------------------------------
  // Map click - add polygon points
  // ----------------------------------------------------

  useEffect(() => {
    if (!map || !isDrawing) return;

    const clickListener = map.addListener("click", (event) => {
      if (!event.latLng) return;

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      // ----------------------------------------------
      // First point
      // ----------------------------------------------

      if (!currentPolygonRef.current) {
        const polygon = new google.maps.Polygon({
          ...polygonOptions,
          map,
          paths: [{ lat, lng }],
        });

        currentPolygonRef.current = polygon;

        polygonRefs.current.push(polygon);

        addPolygonListeners(polygon);

        notifyPolygonChange();

        return;
      }

      // ----------------------------------------------
      // Add next point
      // ----------------------------------------------

      const polygon = currentPolygonRef.current;

      const path = polygon.getPath();

      path.push(new google.maps.LatLng(lat, lng));

      notifyPolygonChange();
    });

    return () => {
      google.maps.event.removeListener(clickListener);
    };
  }, [
    map,
    isDrawing,
    addPolygonListeners,
    notifyPolygonChange,
  ]);

  // ----------------------------------------------------
  // Finish drawing
  // ----------------------------------------------------

  useEffect(() => {
    if (!map || !isDrawing) return;

    const dblClickListener = map.addListener("dblclick", () => {
      const polygon = currentPolygonRef.current;

      if (!polygon) return;

      const coordinates = getPolygonCoordinates(polygon);

      // Need minimum 3 points
      if (coordinates.length < 3) {
        alert("Please select at least 3 points.");

        polygon.setMap(null);

        polygonRefs.current =
          polygonRefs.current.filter(
            (item) => item !== polygon
          );

        currentPolygonRef.current = null;

        notifyPolygonChange();

        return;
      }

      // Drawing completed
      currentPolygonRef.current = null;

      setIsDrawing(false);

      notifyPolygonChange();
    });

    return () => {
      google.maps.event.removeListener(dblClickListener);
    };
  }, [
    map,
    isDrawing,
    getPolygonCoordinates,
    notifyPolygonChange,
  ]);

  // ----------------------------------------------------
  // Places autocomplete
  // ----------------------------------------------------

  useEffect(() => {
    if (!map || !places) return;

    const input = document.getElementById("searchBox");

    if (!input) return;

    const autocomplete = new places.Autocomplete(input);

    autocomplete.bindTo("bounds", map);

    const listener = autocomplete.addListener(
      "place_changed",
      () => {
        const place = autocomplete.getPlace();

        if (!place.geometry) return;

        if (place.geometry.viewport) {
          map.fitBounds(place.geometry.viewport);
        } else {
          map.setCenter(place.geometry.location);
          map.setZoom(15);
        }
      }
    );

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map, places]);

  // ----------------------------------------------------
  // Delete all polygons
  // ----------------------------------------------------

  useEffect(() => {
    if (!shapesDeleted || shapesDeleted <= 0) return;

    polygonRefs.current.forEach((polygon) => {
      polygon.setMap(null);
    });

    polygonRefs.current = [];

    if (currentPolygonRef.current) {
      currentPolygonRef.current.setMap(null);
      currentPolygonRef.current = null;
    }

    setIsDrawing(false);

    onPolygonChange([]);
  }, [shapesDeleted, onPolygonChange]);

  // ----------------------------------------------------
  // Cleanup
  // ----------------------------------------------------

  useEffect(() => {
    return () => {
      // Remove polygons
      polygonRefs.current.forEach((polygon) => {
        polygon.setMap(null);
      });

      polygonRefs.current = [];

      // Remove current polygon
      if (currentPolygonRef.current) {
        currentPolygonRef.current.setMap(null);
        currentPolygonRef.current = null;
      }

      // Remove listeners
      listenersRef.current.forEach((listener) => {
        if (listener) {
          google.maps.event.removeListener(listener);
        }
      });

      listenersRef.current = [];
    };
  }, []);

  // ----------------------------------------------------
  // Render
  // ----------------------------------------------------

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      {/* Search */}

      <input
        id="searchBox"
        type="text"
        placeholder="Search Location"
        style={{
          boxSizing: "border-box",
          border: "1px solid #ccc",
          width: "250px",
          height: "40px",
          padding: "0 12px",
          borderRadius: "6px",
          fontSize: "14px",
          outline: "none",
          position: "absolute",
          right: "10px",
          top: "10px",
          zIndex: 10,
        }}
      />

      {/* Add Location */}

      <button
        type="button"
        onClick={startDrawing}
        style={{
          position: "absolute",
          left: "10px",
          top: "10px",
          zIndex: 10,
          height: "40px",
          padding: "0 15px",
          border: "1px solid #ccc",
          borderRadius: "6px",
          background: "#fff",
          cursor: "pointer",
          fontSize: "14px",
        }}
      >
        {isDrawing ? "Drawing..." : "Add Location"}
      </button>

      {/* Instruction */}

      {isDrawing && (
        <div
          style={{
            position: "absolute",
            left: "10px",
            top: "60px",
            zIndex: 10,
            background: "#fff",
            padding: "8px 12px",
            borderRadius: "6px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            fontSize: "13px",
          }}
        >
          Click on the map to add points.
          <br />
          Double-click to finish.
        </div>
      )}

      {/* Google Map */}

      <Map
        defaultZoom={14}
        defaultCenter={center}
        style={{
          width: "100%",
          height: "450px",
          borderRadius: "10px",
        }}
        onLoad={(mapInstance) => {
          mapRef.current = mapInstance;
        }}
        disableDoubleClickZoom={isDrawing}
      />
    </div>
  );
};

export default MapContainer;